import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { streamChat, type ChatTurn } from './client.js';
import './assistant.css';

interface ToolActivity {
  summary: string;
  ok?: boolean;
}
interface Msg {
  role: 'user' | 'assistant';
  text: string;
  tools?: ToolActivity[];
  thinking?: boolean;
}

export function AssistantPanel() {
  const open = useApp((s) => s.assistantOpen);
  const setOpen = useApp((s) => s.setAssistantOpen);
  const view = useApp((s) => s.view);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const contextPath =
    view.kind === 'note' || view.kind === 'canvas'
      ? (vault.getEntry(view.id)?.path ?? undefined)
      : undefined;

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    const history: ChatTurn[] = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: 'user', text }, { role: 'assistant', text: '' }]);
    setBusy(true);
    const ac = new AbortController();
    abortRef.current = ac;

    const patchLast = (fn: (m: Msg) => Msg) =>
      setMessages((prev) => {
        const next = prev.slice();
        next[next.length - 1] = fn(next[next.length - 1]);
        return next;
      });

    try {
      for await (const ev of streamChat({ message: text, history, contextPath }, ac.signal)) {
        if (ev.type === 'text') patchLast((m) => ({ ...m, text: m.text + ev.text, thinking: false }));
        else if (ev.type === 'thinking') patchLast((m) => ({ ...m, thinking: true }));
        else if (ev.type === 'tool')
          patchLast((m) => ({ ...m, thinking: false, tools: [...(m.tools ?? []), { summary: ev.summary }] }));
        else if (ev.type === 'tool_result')
          patchLast((m) => {
            const tools = (m.tools ?? []).slice();
            for (let i = tools.length - 1; i >= 0; i--)
              if (tools[i].ok === undefined) {
                tools[i] = { ...tools[i], ok: ev.ok };
                break;
              }
            return { ...m, tools };
          });
        else if (ev.type === 'error')
          patchLast((m) => ({ ...m, text: m.text + `\n\n⚠️ ${ev.message}`, thinking: false }));
        else if (ev.type === 'done') break;
      }
    } catch {
      patchLast((m) => ({ ...m, text: m.text + '\n\n⚠️ connection lost', thinking: false }));
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [input, busy, messages, contextPath]);

  if (!open) return null;

  return (
    <div className={`assistant-panel${open ? ' open' : ''}`}>
      <div className="assistant-head">
        <span className="assistant-title">✦ Assistant</span>
        {contextPath && <span className="assistant-ctx" title={contextPath}>on {contextPath.split('/').pop()}</span>}
        <span style={{ flex: 1 }} />
        <button className="icon-btn" title="New chat" onClick={() => { stop(); setMessages([]); }}>🗒</button>
        <button className="icon-btn" title="Close" onClick={() => { stop(); setOpen(false); }}>✕</button>
      </div>

      <div className="assistant-scroll" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="assistant-empty">
            <p>Ask about your notes, or tell me to create or edit them.</p>
            <ul>
              <li>“What did we decide about the GSK stakeholder map?”</li>
              <li>“Turn this email thread into notes” (paste it below)</li>
              <li>“Draft a summary note of my NTAM meetings this week”</li>
            </ul>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`assistant-msg ${m.role}`}>
            {m.tools && m.tools.length > 0 && (
              <div className="assistant-tools">
                {m.tools.map((t, j) => (
                  <div key={j} className="assistant-tool">
                    <span>{t.ok === undefined ? '⏳' : t.ok ? '✓' : '⚠️'}</span> {t.summary}
                  </div>
                ))}
              </div>
            )}
            {m.role === 'assistant' ? (
              <div className="assistant-md">
                {m.text ? (
                  <Markdown remarkPlugins={[remarkGfm]}>{m.text}</Markdown>
                ) : m.thinking ? (
                  <span className="assistant-thinking">thinking…</span>
                ) : (
                  <span className="assistant-thinking">…</span>
                )}
              </div>
            ) : (
              <div className="assistant-user-text">{m.text}</div>
            )}
          </div>
        ))}
      </div>

      <div className="assistant-input-row">
        <textarea
          ref={inputRef}
          className="assistant-input"
          placeholder="Message the assistant…  (⌘⏎ to send)"
          value={input}
          rows={2}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {busy ? (
          <button className="assistant-send stop" onClick={stop} title="Stop">■</button>
        ) : (
          <button className="assistant-send" onClick={() => void send()} disabled={!input.trim()} title="Send">➤</button>
        )}
      </div>
    </div>
  );
}
